import { useState, useRef, useCallback } from 'react';
import type { OutputFormat } from '../../types/floorPlan.types';
import { compressImageOnServer } from '../../utils/compressImageOnServer';

const DEFAULT_SCALE_FACTOR = 0.5;
const DEFAULT_QUALITY = 80;
const DEFAULT_FORMAT: OutputFormat = 'webp';

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const CompressionTest = () => {
  const [scaleFactor, setScaleFactor] = useState(DEFAULT_SCALE_FACTOR);
  const [quality, setQuality] = useState(DEFAULT_QUALITY);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(DEFAULT_FORMAT);
  const [status, setStatus] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      setSelectedFile(file);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setStatus(`Selected: ${file.name} (${fileSizeMB} MB)`);
    },
    [],
  );

  const handleCompress = useCallback(async () => {
    if (!selectedFile) return;

    const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
    setStatus(`Compressing ${selectedFile.name} (${fileSizeMB} MB)...`);
    setIsProcessing(true);

    try {
      const result = await compressImageOnServer({
        file: selectedFile,
        scaleFactor,
        quality,
        outputFormat,
      });

      const compressedSizeMB = (result.blob.size / (1024 * 1024)).toFixed(2);
      const reductionPercent = Math.round((1 - result.blob.size / selectedFile.size) * 100);

      const outputFileName = `compressed_${selectedFile.name.replace(/\.[^.]+$/, '')}.${outputFormat}`;
      downloadBlob(result.blob, outputFileName);

      setStatus(
        `Done! ${result.originalWidth}x${result.originalHeight} → ${result.outputWidth}x${result.outputHeight} | ${fileSizeMB} MB → ${compressedSizeMB} MB (${reductionPercent}% reduction)`,
      );
    } catch (error) {
      setStatus(`Error: ${(error as Error).message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedFile, scaleFactor, quality, outputFormat]);

  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
      <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
        Compression Test
      </h2>
      <div className="flex gap-4 items-end flex-wrap mb-3">
        <label className="text-xs text-[#8b949e]">
          Scale Factor
          <input
            type="number"
            min={0.01}
            max={2}
            step={0.05}
            value={scaleFactor}
            onChange={(e) => setScaleFactor(Number(e.target.value))}
            className="block mt-1 w-24 bg-[#0d1117] border border-[#30363d] rounded px-2 py-1 text-[#c9d1d9]"
          />
        </label>
        <label className="text-xs text-[#8b949e]">
          Quality (1-100)
          <input
            type="number"
            min={1}
            max={100}
            value={quality}
            onChange={(e) => setQuality(Number(e.target.value))}
            className="block mt-1 w-24 bg-[#0d1117] border border-[#30363d] rounded px-2 py-1 text-[#c9d1d9]"
          />
        </label>
        <label className="text-xs text-[#8b949e]">
          Format
          <select
            value={outputFormat}
            onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
            className="block mt-1 bg-[#0d1117] border border-[#30363d] rounded px-2 py-1 text-[#c9d1d9]"
          >
            <option value="webp">WebP</option>
            <option value="jpg">JPEG</option>
            <option value="png">PNG</option>
          </select>
        </label>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelected}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            className="bg-[#30363d] hover:bg-[#484f58] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded text-sm cursor-pointer"
          >
            {selectedFile ? selectedFile.name : 'Pick Image'}
          </button>
          <button
            onClick={handleCompress}
            disabled={!selectedFile || isProcessing}
            className="bg-[#238636] hover:bg-[#2ea043] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded text-sm cursor-pointer"
          >
            {isProcessing ? 'Processing...' : 'Compress'}
          </button>
        </div>
      </div>
      {status && (
        <p className="text-xs text-[#8b949e] mt-2">{status}</p>
      )}
    </div>
  );
};
